/* ==========================================================================
   HEXA Downlight Experience (hx-dlx)

   This file deliberately does very little. Every reveal, bar fill, cable draw
   and hover effect in the hx-dlx sections is pure CSS driven by the shared
   hx-reveal observer. Only three behaviours genuinely need JS:

     1. Counter animation   (counting up cannot be done in CSS reliably)
     2. CCT switcher        (swapping the room temperature on tap)
     3. Pointer glow        (writing cursor position into a CSS variable)

   No dependencies. No jQuery. Guarded so it runs once per page, and re-scans
   on shopify:section:load so the theme editor stays live.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__hxDlxInit) return;
  window.__hxDlxInit = true;

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var HAS_IO = 'IntersectionObserver' in window;

  /* ------------------------------------------------------------------------
     1. COUNTERS
     Counts an element from 0 to data-hx-dlx-to when it scrolls into view.
     Uses requestAnimationFrame and an eased curve so it decelerates rather
     than ticking linearly. Honours decimals via data-hx-dlx-decimals.
     ------------------------------------------------------------------------ */
  var COUNT_MS = 1600;

  function easeOutExpo(t) {
    return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
  }

  function formatNumber(value, decimals) {
    return value.toLocaleString('en-AU', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  }

  function runCounter(el) {
    if (el.__hxDlxCounted) return;
    el.__hxDlxCounted = true;

    var target = parseFloat(el.getAttribute('data-hx-dlx-to'));
    if (isNaN(target)) return;

    var decimals = parseInt(el.getAttribute('data-hx-dlx-decimals') || '0', 10);
    var prefix = el.getAttribute('data-hx-dlx-prefix') || '';
    var suffix = el.getAttribute('data-hx-dlx-suffix') || '';

    // Reduced motion or no rAF: show the final value immediately.
    if (REDUCED || !window.requestAnimationFrame) {
      el.textContent = prefix + formatNumber(target, decimals) + suffix;
      return;
    }

    var start = null;

    function tick(now) {
      if (start === null) start = now;
      var progress = Math.min((now - start) / COUNT_MS, 1);
      var value = target * easeOutExpo(progress);
      el.textContent = prefix + formatNumber(value, decimals) + suffix;
      if (progress < 1) window.requestAnimationFrame(tick);
    }

    window.requestAnimationFrame(tick);
  }

  function initCounters(root) {
    var els = root.querySelectorAll('[data-hx-dlx-to]:not([data-hx-dlx-ready])');
    if (!els.length) return;

    Array.prototype.forEach.call(els, function (el) {
      el.setAttribute('data-hx-dlx-ready', '');

      // Reserve the final width so the card cannot shift as digits are added.
      // The value is set to the target first, measured, then reset to zero.
      if (!HAS_IO) {
        runCounter(el);
        return;
      }

      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              runCounter(entry.target);
              io.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.4 }
      );

      io.observe(el);
    });
  }

  /* ------------------------------------------------------------------------
     2. CCT SWITCHER
     Each section has a tablist of temperature options. Selecting one writes
     the temperature's colours onto the section as CSS variables, and the SVG
     room transitions between them. Also updates the physical switch graphic.

     Keyboard: arrow keys move between options, per the WAI-ARIA tabs pattern.
     ------------------------------------------------------------------------ */
  function selectTemp(section, opt) {
    var opts = section.querySelectorAll('.hx-dlx-cct__opt');
    var index = Array.prototype.indexOf.call(opts, opt);

    Array.prototype.forEach.call(opts, function (o) {
      var selected = o === opt;
      o.setAttribute('aria-selected', selected ? 'true' : 'false');
      // Only the selected option stays in the tab order (roving tabindex).
      o.setAttribute('tabindex', selected ? '0' : '-1');
    });

    // Push this temperature's palette onto the section.
    section.style.setProperty('--hx-dlx-cct-glow', opt.getAttribute('data-hx-dlx-glow') || '#ffd9a8');
    section.style.setProperty('--hx-dlx-cct-wall', opt.getAttribute('data-hx-dlx-wall') || '#f3e7d6');

    /* LIVE ROOM GRADE
       The room is ONE photograph. Each temperature supplies a CSS filter and a
       soft-light tint, and both properties transition, so the room eases from
       warm to cool rather than cutting between images. The values come from
       schema settings, so the grade can be tuned in the theme editor without
       touching this file. */
    section.style.setProperty('--hx-dlx-cct-filter', opt.getAttribute('data-hx-dlx-filter') || 'none');
    section.style.setProperty('--hx-dlx-cct-tint', opt.getAttribute('data-hx-dlx-tint') || 'transparent');
    section.style.setProperty('--hx-dlx-cct-tint-o', opt.getAttribute('data-hx-dlx-tint-o') || '0');

    // Slide the switch actuator to the matching detent.
    section.style.setProperty('--hx-dlx-cct-i', String(index));

    // Light the matching slot on the older switch graphic, when present.
    var slots = section.querySelectorAll('.hx-dlx-cct__switch-slot');
    Array.prototype.forEach.call(slots, function (slot, i) {
      slot.setAttribute('data-active', i === index ? 'true' : 'false');
    });

    // Kelvin readout over the photo.
    var kEl = section.querySelector('[data-hx-dlx-cct-k]');
    var nEl = section.querySelector('[data-hx-dlx-cct-n]');
    if (kEl) kEl.textContent = opt.getAttribute('data-hx-dlx-kelvin') || '';
    if (nEl) nEl.textContent = opt.getAttribute('data-hx-dlx-name') || '';

    // Swap the caption under the room.
    var caption = section.querySelector('[data-hx-dlx-cct-caption]');
    if (caption) caption.textContent = opt.getAttribute('data-hx-dlx-caption') || '';
  }

  function initCct(root) {
    var sections = root.querySelectorAll('.hx-dlx-cct:not([data-hx-dlx-ready])');

    Array.prototype.forEach.call(sections, function (section) {
      section.setAttribute('data-hx-dlx-ready', '');

      var opts = section.querySelectorAll('.hx-dlx-cct__opt');
      if (!opts.length) return;

      Array.prototype.forEach.call(opts, function (opt) {
        opt.addEventListener('click', function () {
          selectTemp(section, opt);
        });

        opt.addEventListener('keydown', function (event) {
          var key = event.key;
          if (key !== 'ArrowRight' && key !== 'ArrowLeft' && key !== 'Home' && key !== 'End') return;

          event.preventDefault();
          var list = Array.prototype.slice.call(opts);
          var i = list.indexOf(opt);
          var next;

          if (key === 'Home') next = 0;
          else if (key === 'End') next = list.length - 1;
          else if (key === 'ArrowRight') next = (i + 1) % list.length;
          else next = (i - 1 + list.length) % list.length;

          list[next].focus();
          selectTemp(section, list[next]);
        });
      });

      // Honour whichever option the merchant marked as the default.
      var initial = section.querySelector('.hx-dlx-cct__opt[aria-selected="true"]') || opts[0];
      selectTemp(section, initial);
    });
  }

  /* ------------------------------------------------------------------------
     3. POINTER GLOW
     Writes the cursor position into --hx-dlx-mx / --hx-dlx-my so the card's
     radial gradient can follow it. Fine pointers only: this is decorative and
     pointless on touch, where it would just cost us a listener per card.
     ------------------------------------------------------------------------ */
  function initGlow(root) {
    if (!window.matchMedia || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    if (REDUCED) return;

    var cards = root.querySelectorAll('.hx-dlx-why__card:not([data-hx-dlx-ready])');

    Array.prototype.forEach.call(cards, function (card) {
      card.setAttribute('data-hx-dlx-ready', '');

      card.addEventListener('pointermove', function (event) {
        var rect = card.getBoundingClientRect();
        card.style.setProperty('--hx-dlx-mx', (event.clientX - rect.left) + 'px');
        card.style.setProperty('--hx-dlx-my', (event.clientY - rect.top) + 'px');
      });
    });
  }

  /* ------------------------------------------------------------------------
     4. HARDWARE SLIDE
     The two product renders start fully outside the section and glide in.

     This runs AUTOMATICALLY: it fires once when the stage comes into view and
     plays on its own timing. It used to be scroll-linked, tracking the wheel
     position frame by frame, which meant the renders only arrived if you kept
     scrolling and crawled backwards if you scrolled up.

     Automatic is also much cheaper: no scroll listener, no rAF loop, no
     per-frame measuring. One IntersectionObserver sets a flag, the observer
     disconnects, and a CSS transition does the work on the compositor.

     The travel distance lives in CSS as a percentage of each render's own
     width (see --hx-dlx-from), so it needs no measuring and stays correct at
     every breakpoint.
     ------------------------------------------------------------------------ */
  function initHardwareSlide(root) {
    var stages = root.querySelectorAll('[data-hx-dlx-hw]:not([data-hx-dlx-ready])');

    Array.prototype.forEach.call(stages, function (stage) {
      stage.setAttribute('data-hx-dlx-ready', '');

      // No observer, or motion is unwelcome: show them in place immediately.
      if (!HAS_IO || REDUCED) {
        stage.setAttribute('data-hx-dlx-in', '');
        return;
      }

      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.setAttribute('data-hx-dlx-in', '');
            io.disconnect(); // one shot: it never needs to run again
          });
        },
        // Wait until a quarter of the stage is showing, so the glide happens
        // while it is actually being looked at rather than off screen.
        { threshold: 0.25 }
      );

      io.observe(stage);
    });
  }

  /* ------------------------------------------------------------------------
     BOOT
     ------------------------------------------------------------------------ */
  function init(root) {
    var scope = root || document;
    initCounters(scope);
    initCct(scope);
    initGlow(scope);
    initHardwareSlide(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  // Theme editor: a section can be re-rendered at any time.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target || document);
  });
})();
